// LIVRAISON_1_6_V3_APPLIED @ 20260715_214326 - fixes OK=3 SKIP=0
import { useState, useCallback } from 'react';
import axios from 'axios';
import { enqueueMutation, cachePut, cacheGet } from '../services/offlineDB';
import { runSync } from '../services/offlineSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Endpoints on which we activate offline queueing for write methods.
 * Reads (GET) on these endpoints are cached for 7 days for offline navigation.
 * Scans/signatures are EXCLUDED (large blobs, kept for Phase B3).
 */
const OFFLINE_QUEUE_PATTERNS = [
  /^\/daily-reports/,         // create, update, stops add/edit/delete
  /^\/inspections/,            // submit inspection
  /^\/service-hours/,          // log hours
  /^\/driver\/assignment/,     // current vehicle/trailers
  /^\/driver\/vehicle/,
  /^\/delivery\/sign/,         // signature client (data URL JSON)
];

const isQueueableEndpoint = (endpoint) =>
  OFFLINE_QUEUE_PATTERNS.some((re) => re.test(endpoint));

const isNetworkError = (err) =>
  !err.response
  || err.code === 'ERR_NETWORK'
  || err.code === 'ECONNABORTED'
  || err.message === 'Network Error'
  || (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout'));

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (method, endpoint, data = null, options = {}) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token')
      || localStorage.getItem('admin_token')
      || sessionStorage.getItem('admin_token');
    const url = `${API_URL}/api${endpoint}`;
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
      // Empêche le HTTP cache navigateur de servir une ancienne réponse (304).
      // Bug historique: la carte admin GPS affichait 0 points même quand
      // le backend renvoyait 500+ points, car le navigateur conservait
      // une réponse vide antérieure.
      ...(method === 'get' && {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      }),
    };
    const config = { method, url, headers, timeout: 15000, ...options };

    if (data) {
      if (method === 'get') config.params = data;
      else config.data = data;
    }

    try {
      const response = await axios(config);
      // Cache GET responses for offline navigation (fire-and-forget, ne bloque plus le return)
      if (method === 'get' && isQueueableEndpoint(endpoint) && navigator.onLine) {
        // Delai 0ms pour laisser React render avant d'ecrire dans IndexedDB
        setTimeout(() => {
          cachePut(`${method}:${endpoint}:${JSON.stringify(data || {})}`, response.data)
            .catch(() => { /* silencieux : cache best-effort */ });
        }, 0);
      }
      return { success: true, data: response.data };
    } catch (err) {
      // OFFLINE FALLBACK
      if (isNetworkError(err) || !navigator.onLine) {
        // GET → return cached data if any
        if (method === 'get' && isQueueableEndpoint(endpoint)) {
          const cached = await cacheGet(`${method}:${endpoint}:${JSON.stringify(data || {})}`);
          if (cached !== null) {
            return { success: true, data: cached, fromCache: true };
          }
        }
        // Mutations (POST/PUT/DELETE/PATCH) → queue for later
        if (method !== 'get' && isQueueableEndpoint(endpoint)) {
          const queueId = await enqueueMutation({
            method,
            url: `/api${endpoint}`, // store relative so token swap on replay works
            data,
            headers: {}, // token is reattached at replay time
            kind: endpoint.split('/').filter(Boolean).slice(0, 2).join('-'),
          });
          // Try to register a Background Sync if the SW supports it
          try {
            if ('serviceWorker' in navigator && 'SyncManager' in window) {
              const reg = await navigator.serviceWorker.ready;
              await reg.sync.register('propilote-mutations');
            }
          } catch {}

          // Special case: POST /daily-reports → return a synthetic "local" report
          // so the app can navigate to it immediately (offline workflow).
          let payload = { queued: true, ...(data || {}) };
          if (method === 'post' && /^\/daily-reports$/.test(endpoint)) {
            const localId = `local-${Date.now()}`;
            payload = {
              id: localId,
              report_number: `LOCAL-${String(queueId).padStart(4, '0')}`,
              date: data?.date || new Date().toISOString().slice(0, 10),
              status: 'draft',
              stops: [],
              _local: true,
              ...(data || {}),
            };
            // Cache the synthetic report so opening it works
            await cachePut(`get:/daily-reports/${localId}:{}`, payload);
            // Also append to the cached list
            const listKey = `get:/daily-reports:{}`;
            const cached = (await cacheGet(listKey)) || [];
            await cachePut(listKey, [payload, ...cached]);
          }

          return {
            success: true,
            data: payload,
            queued: true,
            offline: true,
          };
        }
      }
      // V19: Intercept free tier block and trigger global modal via custom event
      const _detail = err.response?.data?.detail;
      if (err.response?.status === 403 && _detail && typeof _detail === 'object' && _detail.error_code === 'FREE_TIER_LIMIT_REACHED') {
        try { window.dispatchEvent(new CustomEvent('free-tier-blocked', { detail: _detail })); } catch {}
        setError(_detail.message || 'Limite gratuite atteinte');
        return { success: false, error: _detail.message || 'Limite gratuite atteinte', errorCode: 'FREE_TIER_LIMIT_REACHED' };
      }

      const message = err.response?.data?.detail || err.message || 'Une erreur est survenue';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((endpoint, params) => request('get', endpoint, params), [request]);
  const post = useCallback((endpoint, data) => request('post', endpoint, data), [request]);
  const put = useCallback((endpoint, data) => request('put', endpoint, data), [request]);
  const patch = useCallback((endpoint, data) => request('patch', endpoint, data), [request]);
  const del = useCallback((endpoint) => request('delete', endpoint), [request]);

  return {
    loading,
    error,
    get,
    post,
    put,
    patch,
    del,
    clearError: () => setError(null),
  };
};

export default useApi;
